// React wrapper around the branch-scoped permission matcher.
//
// `hasPermission(module, action)` is the app-wide permission check. It
// reads the payload cached in `localStorage['branchPermissions']` (see
// `src/utils/branchPermissions.js`), so the answer always reflects the
// branch the user currently has selected.
//
// The hook subscribes to:
//   - the `mdg:permissions-updated` window event, so a branch switch or a
//     permission write re-renders the sidebar and any gated control
//   - tab focus, which re-fetches `/me` so the account-level facts
//     (`userType`) stay fresh
//
// Pure (non-React) callers should `import { hasPermission }` from
// `src/utils/branchPermissions` directly.

import { useCallback, useEffect, useMemo, useState } from 'react'

import { PERMISSIONS } from '../constants/permission'
import { getBranchPermissionIndex, hasPermission } from '../utils/branchPermissions'
import { refreshCurrentUserMe } from '../utils/permissions'
import { userStorage } from '../utils/userStorage'

import { PERMISSIONS_UPDATED_EVENT } from './usePermissionEvents'

/**
 * @returns {{
 *   permissions: object | null,
 *   hasPermission: (module: string | number | object, action?: string) => boolean,
 *   isPermissionActiveForRole: (module: string | number | object, action?: string) => boolean,
 *   isLoaded: boolean,
 *   userType: string | undefined,
 *   userDetails: object | null,
 * }}
 */
export function useEffectivePermissions() {
  const [userDetails, setUserDetails] = useState(() => userStorage.getUserDetails() || null)
  const [isLoaded, setIsLoaded] = useState(false)
  // Bumped on every PERMISSIONS_UPDATED_EVENT so the memoised permission
  // index is re-read from localStorage.
  const [permissionsTick, setPermissionsTick] = useState(0)

  // Mark `isLoaded` on the next tick so the first render never collapses
  // a sidebar item before React knows the user payload exists.
  useEffect(() => {
    setIsLoaded(true)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const handler = () => {
      setUserDetails(userStorage.getUserDetails() || null)
      setPermissionsTick((n) => n + 1)
    }
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, handler)

    // Tab focus — re-fetch /me so role changes made in another tab
    // (or by an admin while we were away) propagate immediately.
    // `refreshCurrentUserMe()` itself enforces the FOCUS_REFRESH_TTL_MS
    // throttle + in-flight guard, so we can fire it unconditionally here.
    const onFocus = () => {
      refreshCurrentUserMe().catch(() => {
        /* non-fatal — cache stays put */
      })
    }
    window.addEventListener('focus', onFocus)

    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, handler)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  // Re-derive the permission index on each permission change so consumers
  // can do bulk lookups.
  const permissions = useMemo(() => getBranchPermissionIndex(), [permissionsTick])
  const userType = userDetails?.type

  // Thin wrapper around the sync matcher — the underlying call already
  // reads from localStorage on every invocation, so memoization buys
  // nothing. The `permissionsTick` dependency forces a fresh function
  // identity (and therefore a re-render) whenever the payload changes.
  const check = useCallback(
    (module, action = PERMISSIONS.VIEW) => hasPermission(module, action),
    [permissionsTick]
  )

  return {
    permissions,
    hasPermission: check,
    // Sibling-compatible alias so call sites can stay terse:
    //   `isPermissionActiveForRole(MODULES.ALARMS, PERMISSIONS.VIEW)`
    // without depending on React.
    isPermissionActiveForRole: check,
    isLoaded,
    userType,
    userDetails,
  }
}

export default useEffectivePermissions
